// Rebuild de packages/hce/src/index.tsx SIN MantineProvider/@medplum/react —
// mismo MedplumClient auditado (storagePrefix, cacheTime: 0,
// onUnauthenticated, fetch auditado), pero con MedplumProvider de
// @medplum/react-hooks (paquete de solo hooks, sin Mantine) y Toaster de
// sonner en vez de Notifications de Mantine.
// Fuentes autoalojadas: no se pide nada a Google (privacidad por defecto).
import '@fontsource/ibm-plex-mono/latin-400.css';
import '@fontsource/ibm-plex-mono/latin-500.css';
import '@fontsource/ibm-plex-sans/latin-400.css';
import '@fontsource/ibm-plex-sans/latin-500.css';
import '@fontsource/ibm-plex-sans/latin-600.css';
import '@fontsource/ibm-plex-sans/latin-700.css';
import type { MedplumClient } from '@medplum/core';
import { crearMedplumClient } from './lib/medplumClienteLote';
import { MedplumProvider } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router';
import { App } from './App';
import { getConfig } from './lib/config';
import { crearFetchAuditado, registrarMedplumParaAuditoria, sincronizarPendientes } from './lib/audit/auditedFetch';
import { normalizarPathnameBasename } from './lib/normalizarBasename';
import { RUTAS_HCE_ADMIN } from './lib/rutasHceAdmin';
import './i18n';
import './styles.css';

const config = getConfig();

// Único lugar donde vive el basename real — reusado por el <BrowserRouter>
// de más abajo y por el redirect de `onUnauthenticated` (bug real
// encontrado en vivo 2026-09-02: el redirect armaba `/login` sin este
// prefijo, así que en modo subdominio, donde Caddy sirve hce-chart bajo
// este path, el propio Router de la app no podía matchear la URL
// resultante y la pantalla quedaba en blanco).
const BASENAME = '/hce/paciente';

// Caddy manda TODO el tráfico del subdominio (cualquier path, incluida la
// raíz "/") a este mismo contenedor — así que alguien que entra directo a
// `demo.logiacare.com` (sin el prefijo) hace que el Router de más abajo no
// matchee nada y la app quede en blanco (mismo síntoma que el bug del
// redirect de login de arriba, encontrado en vivo el mismo día). Si el
// path actual no tiene el prefijo, se reescribe UNA vez antes de montar
// nada — nunca se llega a crear el `MedplumClient` ni a renderizar un
// Router roto.
//
// Salvaguarda extra: si el path YA trae el prefijo repetido más de una vez
// (`/hce/paciente/hce/paciente/...` — bug real de `onUnauthenticated`
// arreglado más abajo, pero un link viejo guardado de antes del fix, o
// cualquier otra vía no prevista, puede seguir mandando uno duplicado), se
// colapsa a un solo prefijo acá antes de montar nada, en vez de dejar que
// se siga acumulando.
const pathname = normalizarPathnameBasename(window.location.pathname, BASENAME);
if (pathname !== window.location.pathname) {
  window.location.replace(pathname + window.location.search + window.location.hash);
} else if (!pathname.startsWith(BASENAME)) {
  window.location.replace(BASENAME + pathname + window.location.search + window.location.hash);
} else {
  iniciarApp();
}

function iniciarApp(): void {
  const medplum = crearMedplumClient({
    baseUrl: config.baseUrl,
    clientId: config.clientId,
    storagePrefix: '@medplum:',
    // cacheTime en 0 a propósito — ver auditedFetch.ts: una lectura servida
    // desde caché nunca pasa por `fetch`, así que nunca se auditaría.
    cacheTime: 0,
    autoBatchTime: 100,
    fetch: crearFetchAuditado(),
    onUnauthenticated: () => {
      // Rediseño mayor 2026-08-31 (Módulo Proxy) — redirige a `/login`
      // PROPIO de hce-chart, nunca a `/signin` de packages/hce (Medplum):
      // el navegador no debe aparecer nunca en la app vieja. Estas rutas
      // NUNCA dependen de una sesión de Medplum (login propio del HCE, o el
      // panel de admin que usa su propio token de hce-auth.ts) — sin esta
      // lista, el primer chequeo de perfil sin sesión (arranque normal, nadie
      // logueado todavía) redirigiría ANTES de que React Router llegue a
      // montarlas. Sumar acá cualquier ruta nueva que no dependa de Medplum.
      // '/login' es la única ruta real del acceso — LandingAcceso.tsx
      // resuelve login/recuperación como estados internos de esa pantalla.
      const RUTAS_SIN_MEDPLUM = ['/login', ...RUTAS_HCE_ADMIN];
      // Rutas con parámetros (2026-09-13): los enlaces de correo que arma
      // Medplum (`setpassword/<id>/<secret>`, `verifyemail/<id>/<secret>`)
      // se abren SIN sesión — sin esto el primer 401 de cualquier componente
      // de fondo mandaría al login antes de mostrar el formulario.
      // `activar/<token>` (2026-09-13): enlace de activación de cuenta que
      // manda la bóveda cuando el admin crea la cuenta — tampoco hay sesión.
      const PREFIJOS_SIN_MEDPLUM = [`${BASENAME}/setpassword/`, `${BASENAME}/verifyemail/`, `${BASENAME}/activar/`];
      const p = window.location.pathname;
      if (!RUTAS_SIN_MEDPLUM.some((r) => p.endsWith(r)) && !PREFIJOS_SIN_MEDPLUM.some((prefijo) => p.startsWith(prefijo))) {
        // Bug real encontrado en logs de producción (Caddy access.log,
        // 2026-09-02/03): `next` viajaba CON el prefijo BASENAME, pero
        // `destinoTrasLogin` (LandingAcceso.tsx) lo pasa tal cual al
        // `navigate()` de React Router — bajo `basename={BASENAME}` (más
        // abajo) eso espera un path YA SIN el prefijo, que el propio Router
        // vuelve a anteponer solo. El resultado: cada vez que la sesión
        // expiraba a mitad de navegación y el usuario volvía a loguearse,
        // terminaba en `/hce/paciente/hce/paciente/...` duplicado. Se
        // guarda acá ya sin el prefijo, coherente con cómo navegan el resto
        // de los `navigate()`/`<Link>` internos de la app.
        let pathnameSinBasename = p;
        while (pathnameSinBasename.startsWith(BASENAME)) {
          pathnameSinBasename = pathnameSinBasename.slice(BASENAME.length);
        }
        pathnameSinBasename ||= '/';
        window.location.href = `${BASENAME}/login?next=` + encodeURIComponent(pathnameSinBasename + window.location.search);
      }
    },
  });
  registrarMedplumParaAuditoria(medplum);

  window.addEventListener('online', () => void sincronizarPendientes());
  void sincronizarPendientes();

  if (import.meta.env.DEV) {
    // Conveniencia de desarrollo local únicamente — permite loguear desde la
    // consola del navegador (medplum.startLogin/processCode) cuando no hay
    // sesión compartida con packages/hce (orígenes/puertos distintos en dev).
    (window as unknown as { medplum: MedplumClient }).medplum = medplum;
  }

  function MedplumProviderConNavegacion({ children }: { children: JSX.Element }): JSX.Element {
    const navigate = useNavigate();
    return (
      <MedplumProvider medplum={medplum} navigate={(path) => void navigate(path)}>
        {children}
      </MedplumProvider>
    );
  }

  const root = createRoot(document.getElementById('root') as HTMLElement);
  root.render(
    <StrictMode>
      <BrowserRouter basename={BASENAME}>
        <MedplumProviderConNavegacion>
          <App />
        </MedplumProviderConNavegacion>
      </BrowserRouter>
    </StrictMode>
  );
}
